import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, X, Users, User, ScrollText } from 'lucide-react';
import { codexApi } from '../api';
import { celebrate } from '../../lib/celebrate';

const OCCASIONS = ['encouragement', 'milestone', 'exhortation', 'comfort', 'celebration'];

/** Modal that lets a mentor compose a Sacred Letter to one or many mentees. */
export default function ComposeLetterModal({ open, onClose, mentees, defaultMenteeId, onSent }) {
  const [recipients, setRecipients] = useState(() => defaultMenteeId ? [defaultMenteeId] : []);
  const [allMentees, setAllMentees] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [occasion, setOccasion] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (open && defaultMenteeId) setRecipients([defaultMenteeId]);
  }, [open, defaultMenteeId]);

  const toggleRecipient = (id) => {
    setRecipients((r) => r.includes(id) ? r.filter((x) => x !== id) : [...r, id]);
  };

  const reset = () => {
    setRecipients(defaultMenteeId ? [defaultMenteeId] : []);
    setAllMentees(false); setTitle(''); setBody(''); setOccasion(null);
  };

  const submit = async () => {
    if (!title.trim() || !body.trim()) return;
    if (!allMentees && recipients.length === 0) return;
    setSubmitting(true);
    try {
      const created = await codexApi.sendLetter({
        title: title.trim(),
        body: body.trim(),
        occasion,
        send_to_all: allMentees || undefined,
        mentee_ids: !allMentees ? recipients : undefined,
      });
      celebrate(0.5);
      reset();
      onSent?.(created);
      onClose();
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(11,10,22,0.88)', backdropFilter: 'blur(8px)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          data-testid="codex-compose-letter"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: 'spring', damping: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="codex-card-gold codex-card"
            style={{ padding: 28, maxWidth: 720, width: '100%', maxHeight: '92vh', overflowY: 'auto' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
              <ScrollText size={22} color="var(--codex-gold)" />
              <div>
                <div className="codex-eyebrow">SEND A SACRED LETTER</div>
                <h2 className="codex-h2" style={{ marginTop: 4 }}>Encourage your mentees</h2>
              </div>
              <button onClick={onClose} className="codex-btn codex-btn-ghost" style={{ marginLeft: 'auto', padding: '6px 10px' }}>
                <X size={14} />
              </button>
            </div>

            {/* Recipient selector */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={allMentees}
                  onChange={(e) => setAllMentees(e.target.checked)}
                  data-testid="codex-letter-send-all"
                />
                <Users size={14} /> Send to ALL my mentees ({mentees?.length || 0})
              </label>

              {!allMentees && (
                <div className="codex-card" style={{ padding: 12, maxHeight: 160, overflowY: 'auto', background: 'rgba(0,0,0,0.2)' }}>
                  {(mentees || []).map((m, idx) => {
                    const checked = recipients.includes(m.user_id);
                    return (
                      <label
                        key={`${m.user_id}-${idx}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, cursor: 'pointer' }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRecipient(m.user_id)}
                          data-testid={`codex-letter-recipient-${m.user_id}`}
                        />
                        <User size={13} />
                        <span>{m.name}</span>
                        <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.55 }}>{m.email}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title — e.g. 'To my faithful Scribe'"
              maxLength={120}
              className="codex-input"
              style={{ marginBottom: 12, fontFamily: 'Cinzel, serif' }}
              data-testid="codex-letter-title"
            />

            <textarea
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write from the heart…"
              maxLength={3000}
              className="codex-input"
              style={{ marginBottom: 12, lineHeight: 1.7, fontFamily: 'Georgia, serif', resize: 'vertical' }}
              data-testid="codex-letter-body"
            />

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
              {OCCASIONS.map((o) => (
                <button
                  key={o}
                  onClick={() => setOccasion(occasion === o ? null : o)}
                  className="codex-card"
                  style={{
                    padding: '6px 10px',
                    fontSize: 12,
                    cursor: 'pointer',
                    color: 'var(--codex-parchment)',
                    borderColor: occasion === o ? 'rgba(212,175,55,0.7)' : undefined,
                    background: occasion === o ? 'rgba(212,175,55,0.15)' : undefined,
                    textTransform: 'capitalize',
                  }}
                >
                  {o}
                </button>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={onClose} className="codex-btn codex-btn-ghost">Cancel</button>
              <button
                onClick={submit}
                disabled={submitting || !title.trim() || !body.trim() || (!allMentees && recipients.length === 0)}
                className="codex-btn codex-btn-primary"
                style={{ opacity: (submitting || !title.trim() || !body.trim() || (!allMentees && recipients.length === 0)) ? 0.4 : 1 }}
                data-testid="codex-letter-send"
              >
                <Send size={14} /> {submitting ? 'Sealing…' : `Send${allMentees ? ' to all' : recipients.length > 1 ? ` to ${recipients.length}` : ''}`}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
